import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit } from "react-icons/fa";
import { MdNewspaper, MdRemoveRedEye } from "react-icons/md";
import { useDispatch } from "react-redux";

import { ReactComponent as StudentPlaceholder } from "../../../assets/Group320.svg";
import { ReactComponent as ClassroomPlaceholder } from "../../../assets/Group322.svg";
import { ReactComponent as MaterialsResourcesIcon } from "../../../assets/MaterialsResources.svg";
import stars_icon from "../../../assets/stars_transparent.png";
import user_placeholder from "../../../assets/user-profile.jpg";
import Button from "../../../components/Button";
import EditLoggedUserModal from "../../../components/modals/common/EditLoggedUserModal";
import { USER_TYPE } from "../../../constants/common";
import useAuth from "../../../hooks/useAuth";
import { setUser } from "../../../redux/slices/userSlice";
import authServices from "../../../services/authServices";
import teacherServices from "../../../services/teacherServices";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const TeacherDashboard = () => {
  const user = useAuth();
  const isThirdPartyUser = user?.is_b12;
  const { t } = useTranslation();
  const lang = getLanguage();
  const navigate = useNavigateWithLang();
  const dispatch = useDispatch();
  const editLoggedUserModalRef = useRef();

  const [teacherCourses, setTeacherCourses] = useState([]);
  const [counts, setCounts] = useState({
    resources_count: 0,
    classrooms_count: 0,
    students_count: 0,
  });
  const [hasTopTen, setHasTopTen] = useState(false);
  const [loading, setLoading] = useState(true);

  const openEditLoggedUserModal = (user) => editLoggedUserModalRef.current.openModal(user);

  const handleSave = async () => {
    const res = await authServices.me();
    const filteredValues = {
      name: res.name,
      username: res.username,
      email: res.email,
      phone: res.phone,
      img_uri: res.img_uri,
    };
    const updatedUser = { ...user, ...filteredValues };
    dispatch(setUser(updatedUser));
  };

  const getTeacherStats = async () => {
    try {
      setLoading(true);
      const response = await teacherServices.getTeacherStats();
      setCounts({
        resources_count: response.resources_count || 0,
        classrooms_count: response.classrooms_count || 0,
        students_count: response.students_count || 0,
      });
      setHasTopTen(response?.top_students?.is_has);
    } catch (error) {
      console.error("Error fetching teacher stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTeacherCourses = async () => {
    try {
      const response = await teacherServices.getTeacherCourses();
      setTeacherCourses(response);
    } catch (error) {
      console.error("Error fetching teacher stats:", error);
    }
  };

  useEffect(() => {
    getTeacherStats();
    getTeacherCourses();
  }, [lang]);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
      {/* teacher Info Card */}

      <div className="col-span-1 justify-between rounded-xl bg-white p-6 shadow-md transition-shadow duration-300 ease-in-out dark:bg-dark-surface md:col-span-3 lg:col-span-3">
        <div className="flex items-center justify-end">
          <Button variant="text" icon={<FaEdit size={20} />} onClick={() => openEditLoggedUserModal(user)} />
        </div>
        <div className="flex flex-col items-center gap-6 lg:flex-row">
          <img
            className="h-28 w-28 rounded-full border-4 border-blue-500 object-cover"
            src={user.img_uri || user_placeholder}
            alt="User"
          />
          <div className="flex-1">
            <h2 className="text-xl font-semibold capitalize text-text dark:text-white">{user?.name}</h2>
            {!isThirdPartyUser && (
              <p className="text-sm text-mutedText dark:text-dark-mutedText">{user?.username}</p>
            )}
            <p className="text-sm text-mutedText dark:text-dark-mutedText">{user?.email}</p>
            <p className="text-sm text-mutedText dark:text-dark-mutedText">{user?.phone}</p>
          </div>
          {hasTopTen && (
            <div
              onClick={() => navigate(`/${USER_TYPE.TEACHER.ROLE}/top-10-students`)}
              className="mt-4 cursor-pointer transition-transform hover:scale-105 lg:mt-0"
            >
              <img src={stars_icon} alt="Top 10" className="h-20 w-auto" />
            </div>
          )}
        </div>
      </div>

      {/* Card Components */}
      {[
        {
          label: t("my_classrooms"),
          count: counts.classrooms_count,
          icon: <ClassroomPlaceholder className="h-12 w-12 text-blue-500" />,
          link: `/${USER_TYPE.TEACHER.ROLE}/my-classrooms`,
        },
        {
          label: t("students"),
          count: counts.students_count,
          icon: <StudentPlaceholder className="h-12 w-12 text-blue-500" />,
          link: `/${USER_TYPE.TEACHER.ROLE}/students`,
        },
        {
          label: t("materials_resources"),
          count: "",
          icon: <MaterialsResourcesIcon className="h-16 w-16 text-blue-500" />,
          link: `/${USER_TYPE.TEACHER.ROLE}/materials-resources`,
        },
      ].map((item, index) => (
        <div
          key={index}
          onClick={() => navigate(item.link)}
          className="flex cursor-pointer flex-col items-center justify-center rounded-xl bg-white p-6 shadow-md transition-transform duration-200 hover:scale-[1.03] dark:bg-dark-surface"
        >
          {item.icon}
          {index === 0 || index === 1 ? (
            loading ? (
              <div className="mt-5 h-7 w-7 animate-spin rounded-full border-2 border-blue-500 border-t-[#e3e3e3]"></div>
            ) : (
              <p className="mt-3 text-3xl font-bold text-blue-500">{item.count}</p>
            )
          ) : (
            <p className="mt-3 text-3xl font-bold text-blue-500">{item.count}</p>
          )}{" "}
          <p className="mt-1 text-center font-semibold text-gray-700 dark:text-white">{item.label}</p>
          {item.extra}
        </div>
      ))}

      {/* <div
        onClick={() => window.open("/dummy_certificate.pdf", "_blank")}
        className="flex cursor-pointer flex-col items-center rounded-xl bg-white p-6 shadow-md transition-transform duration-200 hover:scale-[1.03] dark:bg-dark-surface"
      >
        <MdNewspaper className="h-12 w-12 text-yellow-500" />
        <p className="mt-1 text-center font-semibold text-gray-700 dark:text-white">
          {t("teacher_certificate")}
        </p>
      </div> */}

      <EditLoggedUserModal ref={editLoggedUserModalRef} onSubmit={handleSave} />
    </div>
  );
};

export default TeacherDashboard;
